import axios from 'axios';
import Papa from 'papaparse';

const API_URL =
    (import.meta?.env?.VITE_API_URL || process.env.VUE_APP_API_URL) ||
    'http://localhost:8000/api';

const DEBUG = import.meta?.env?.VITE_DEBUG === 'true' || process.env.VUE_APP_DEBUG === 'true';



const axiosInstance = axios.create({
    baseURL: API_URL,
});

axiosInstance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }

        // Don't override Content-Type if it's already set (e.g., for multipart/form-data)
        if (!config.headers['Content-Type'] && !config.headers['content-type']) {
            config.headers['Content-Type'] = 'application/json';
        }

        if (DEBUG) {
            console.log('Request:', {
                method: config.method,
                url: config.url,
                headers: config.headers,
                data: config.data instanceof FormData
                    ? '[FormData Object]'
                    : config.data
            });
        }

        return config;
    },
    (error) => {
        if (DEBUG) {
            console.error('Request error:', error);
        }
        return Promise.reject(error);
    }
);

axiosInstance.interceptors.response.use(
    (response) => {
        if (DEBUG) {
            console.log('Response:', {
                status: response.status,
                data: response.data
            });
        }
        return response;
    },
    (error) => {
        if (error.response) {
            if (DEBUG) {
                console.error('Error response:', {
                    status: error.response.status,
                    data: error.response.data
                });
            }

            if (error.response.status === 401) {
                console.error('Authentication error:', error.response.data);
            }
        } else if (error.request) {
            if (DEBUG) {
                console.error('No response received:', error.request);
            }
        } else {
            if (DEBUG) {
                console.error('Error setting up request:', error.message);
            }
        }

        return Promise.reject(error);
    }
);

export default {
    async parseCsv(file, delimiter = ',') {
        if (DEBUG) {
            console.log('Parsing CSV file', file.name, 'with delimiter', delimiter);
        }

        return new Promise((resolve, reject) => {
            Papa.parse(file, {
                header: true,
                delimiter,
                skipEmptyLines: true,
                transformHeader: (header) => {
                    // Trim headers to avoid whitespace issues
                    return header.trim();
                },
                complete: (results) => {
                    if (DEBUG) {
                        console.log('CSV parse complete:', results);
                    }

                    // Validate parsed data
                    if (results.errors && results.errors.length > 0) {
                        if (DEBUG) {
                            console.warn('CSV parse warnings/errors:', results.errors);
                        }
                    }

                    if (!results.data || results.data.length === 0) {
                        reject(new Error('No data found in CSV file'));
                        return;
                    }

                    resolve(results.data);
                },
                error: (err) => {
                    if (DEBUG) {
                        console.error('CSV parse error:', err);
                    }
                    reject(err);
                },
            });
        });
    },

    // This function is a placeholder for parsing XLSX files.
    async parseXlsx(file) {
        if (DEBUG) {
            console.log('XLSX parsing not fully implemented, falling back to CSV parser');
        }
        return this.parseCsv(file);
    },

    async parseJson(file) {
        if (DEBUG) {
            console.log('Parsing JSON file', file.name);
        }

        try {
            const content = await file.text();
            const parsed = JSON.parse(content);
            if (DEBUG) {
                console.log('JSON parse complete');
            }
            return parsed;
        } catch (error) {
            if (DEBUG) {
                console.error('JSON parse error:', error);
            }
            throw new Error(`Failed to parse JSON file: ${error.message}`);
        }
    },

    async uploadMeteringPoints(meteringPoints) {
        if (DEBUG) {
            console.log('Uploading metering points as JSON payload:', meteringPoints);
        }

        if (!meteringPoints || meteringPoints.length === 0) {
            throw new Error('No metering points to upload');
        }

        // Validate required fields before sending
        const invalidPoints = meteringPoints.filter(mp =>
            !mp.mpid || !mp.street || !mp.city || !mp.customer_fname
        );

        if (invalidPoints.length > 0) {
            if (DEBUG) {
                console.error('Invalid metering points detected:', invalidPoints);
            }
            throw new Error(`${invalidPoints.length} metering points are missing required fields`);
        }

        try {
            // This sends the request to API_URL/meteringpoints/import with the token attached
            const response = await axiosInstance.post('/meteringpoints/import', { meteringPoints });
            if (DEBUG) {
                console.log('Upload successful:', response.data);
            }
            return response;
        } catch (error) {
            if (DEBUG) {
                console.error('Upload failed:', error.response?.data || error.message);
            }
            throw error;
        }
    },

    async uploadMeteringPointsFile(file, importType, delimiter = ',', attachToDevice = false) {
        if (DEBUG) {
            console.log('Uploading file:', file.name, 'type:', importType, 'delimiter:', delimiter, 'attachToDevice:', attachToDevice);
        }

        // Create a FormData instance for file upload
        const formData = new FormData();
        formData.append('file', file);
        formData.append('importType', importType);
        formData.append('attachToDevice', attachToDevice.toString());

        if (importType === 'csv') {
            formData.append('delimiter', delimiter);
        }

        try {
            // Override the Content-Type header for multipart/form-data
            const response = await axiosInstance.post('/meteringpoints/import', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (DEBUG) {
                console.log('File upload successful:', response.data);
            }

            return response;
        } catch (error) {
            if (DEBUG) {
                console.error('File upload failed:', error.response?.data || error.message);
            }
            throw error;
        }
    },

    downloadTemplate(delimiter = ',', includeDeviceColumn = false) {
        if (DEBUG) {
            console.log('Downloading CSV template with delimiter:', delimiter, 'includeDeviceColumn:', includeDeviceColumn);
        }

        // Define base headers
        let headers = ['MPID', 'Street', 'City', 'ZipCode', 'CustomerFName', 'Country', 'Lat', 'Lon'];
        let exampleRow = ['MP12345', '123 Main St', 'New York', '10001', 'John Smith', 'USA', '40.7128', '-74.0060'];

        // Add LogicalDeviceNumber column if device attachment is enabled
        if (includeDeviceColumn) {
            headers.push('LogicalDeviceNumber');
            exampleRow.push('DEV001');
        }

        // Create CSV content with the selected delimiter
        let csvContent = headers.join(delimiter) + '\n';
        csvContent += exampleRow.join(delimiter) + '\n';

        // Create a Blob with the CSV content
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

        // Create a download link and trigger the download
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        // Determine delimiter name for the filename
        let delimiterName = 'comma';
        if (delimiter === ';') {
            delimiterName = 'semicolon';
        } else if (delimiter === '\t') {
            delimiterName = 'tab';
        }

        const deviceSuffix = includeDeviceColumn ? '_with_device' : '';
        link.setAttribute('href', url);
        link.setAttribute('download', `metering_points_template_${delimiterName}${deviceSuffix}.csv`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        if (DEBUG) {
            console.log('CSV template downloaded');
        }
    },

    downloadXlsxTemplate(includeDeviceColumn = false) {
        if (DEBUG) {
            console.log('Downloading XLSX template, includeDeviceColumn:', includeDeviceColumn);
        }

        // Define base headers
        let headers = ['MPID', 'Street', 'City', 'ZipCode', 'CustomerFName', 'Country', 'Lat', 'Lon'];
        let exampleRow = ['MP12345', '123 Main St', 'New York', '10001', 'John Smith', 'USA', '40.7128', '-74.0060'];

        // Add LogicalDeviceNumber column if device attachment is enabled
        if (includeDeviceColumn) {
            headers.push('LogicalDeviceNumber');
            exampleRow.push('DEV001');
        }

        // Create CSV content (using comma as delimiter for Excel compatibility)
        let csvContent = headers.join(',') + '\n';
        csvContent += exampleRow.join(',') + '\n';

        // Create a Blob with the CSV content
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

        // Create a download link and trigger the download
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        const deviceSuffix = includeDeviceColumn ? '_with_device' : '';
        link.setAttribute('href', url);
        link.setAttribute('download', `metering_points_template${deviceSuffix}.xlsx`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        if (DEBUG) {
            console.log('XLSX template downloaded');
        }

        // Show instructions to the user - you would need to handle this in the component
        return 'The template will download as a CSV file. Please open it in Excel and save as .xlsx format for a proper Excel template.';
    },

    downloadJsonTemplate(includeDeviceColumn = false) {
        if (DEBUG) {
            console.log('Downloading JSON template, includeDeviceColumn:', includeDeviceColumn);
        }

        const example = [
            {
                mpid: 'MP12345',
                street: '123 Main St',
                city: 'New York',
                zip_code: '10001',
                customer_fname: 'John Smith',
                country: 'USA',
                lat: 40.7128,
                lon: -74.0060,
                ...(includeDeviceColumn && { logical_device_number: 'DEV001' })
            }
        ];

        const jsonStr = JSON.stringify({ meteringPoints: example }, null, 2);
        const blob = new Blob([jsonStr], { type: 'application/json;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        const deviceSuffix = includeDeviceColumn ? '_with_device' : '';
        link.setAttribute('href', url);
        link.setAttribute('download', `metering_points_template${deviceSuffix}.json`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        if (DEBUG) {
            console.log('JSON template downloaded');
        }
    }
};